import BankingService from 'services/Banking'
import Statement from '../Statement'
import MainButton from 'components/Buttons/MainButton'
import { MdPix } from 'react-icons/md'
import { FaBarcode, FaUniversity, FaExchangeAlt, FaRegClock } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Container,
  Content,
  Card,
  CardHeader,
  CardTitle,
  HeroCard,
  HeroLabel,
  HeroBalance,
  HeroFooter,
  HeroDetail,
  DataList,
  DataRow,
  DataLabel,
  StatusBadge,
  Dot,
  QuickActionGrid,
  QuickAction,
  QuickActionIcon,
  Caption,
  InfoText,
  MoneyTextMask
} from '../styles'

/*
  Atalhos do cartão de saldo, e o único caminho para cada operação desde que a aba de
  movimentação deixou de existir — quem abre a área de banking quase sempre já sabe o que
  veio fazer, e escolher a operação duas vezes era o que sobrava da versão de abas.

  O Pix não abre um formulário e sim a área dele, com as formas de pagar: copia e cola e
  chave. É o desenho dos bancos digitais, e o mesmo dos apps.
*/
const QUICK_ACTIONS = [
  {value: 'withdraw', label: 'Sacar', Icon: FaUniversity},
  {value: 'pix', label: 'Pix', Icon: MdPix},
  {value: 'transfer', label: 'Transferir', Icon: FaExchangeAlt},
  {value: 'bill', label: 'Boleto', Icon: FaBarcode}
]

/*
  A conta vem da página: a aba do saldo só é montada depois que ela existe, então aqui não
  há mais o caso "ainda não tenho conta" para tratar.
*/
export default function Balance ({
  account,
  requestConfig,
  canTransfer = true,
  onTabNavigate = () => {},
  onOperationNavigate = () => {}
}) {
  /*
    `availableBalance` é o saldo menos os débitos já iniciados e ainda não liquidados —
    é o que o titular realmente pode gastar agora.
  */
  const balance = Number (account.balance ?? 0)
  const availableBalance = Number (account.availableBalance ?? balance)
  const pendingDebits = Number ((balance - availableBalance).toFixed (2))
  const updatedAt = account.balanceUpdatedAt
    ? DateTime.fromISO (account.balanceUpdatedAt).setLocale ('pt-BR')
    : null

  return (
    <Container>
      <Content>
        <HeroCard>
          <HeroLabel>Saldo disponível</HeroLabel>
          <HeroBalance value={availableBalance}/>
          <HeroFooter>
            {account.agency && (
              <HeroDetail>
                <span>Agência e conta</span>
                <b>{`${account.agency} / ${account.account}-${account.accountDigit}`}</b>
              </HeroDetail>
            )}
            {updatedAt && (
              <HeroDetail>
                <span>Atualizado</span>
                <b>{updatedAt.toFormat ("dd/MM 'às' HH:mm")}</b>
              </HeroDetail>
            )}
          </HeroFooter>
        </HeroCard>

        {!account.canTransact && (
          <Card gap={10}>
            <StatusBadge color={BankingService.getStatusColor (account.status)}>
              <Dot/>
              {BankingService.getStatusLabel (account.status)}
            </StatusBadge>
            <Caption>
              {'Sua conta ainda está em análise. Você será avisado assim que ela for '
              + 'aprovada e puder movimentar o saldo'}
            </Caption>
            <MainButton
              label='Ver meus dados'
              onClick={() => onTabNavigate ('account_data')}
              width={180}
            />
          </Card>
        )}

        {account.canTransact && canTransfer && BankingService.canMoveMoney () && (
          <QuickActionGrid>
            {QUICK_ACTIONS.map (({ value, label, Icon }) => (
              <QuickAction key={value} type='button' onClick={() => onOperationNavigate (value)}>
                <QuickActionIcon>
                  <Icon size={16} color='#252525'/>
                </QuickActionIcon>
                <span>{label}</span>
              </QuickAction>
            ))}
          </QuickActionGrid>
        )}

        <Card gap={12}>
          <CardHeader>
            <CardTitle>Resumo</CardTitle>
            <StatusBadge color={BankingService.getStatusColor (account.status)}>
              <Dot/>
              {BankingService.getStatusLabel (account.status)}
            </StatusBadge>
          </CardHeader>
          <DataList>
            <DataRow>
              <DataLabel>Saldo total</DataLabel>
              <MoneyTextMask value={balance}/>
            </DataRow>
            <DataRow>
              <DataLabel>Débitos em andamento</DataLabel>
              <MoneyTextMask value={pendingDebits}/>
            </DataRow>
            <DataRow>
              <DataLabel>Disponível para movimentar</DataLabel>
              <MoneyTextMask value={availableBalance}/>
            </DataRow>
          </DataList>
          {pendingDebits > 0 && (
            <InfoText>
              <FaRegClock size={11} style={{marginRight: 6, marginBottom: -1}}/>
              {'Débitos em andamento são operações já iniciadas que ainda não foram '
              + 'liquidadas pelo banco. Elas já estão descontadas do disponível'}
            </InfoText>
          )}
        </Card>
        <Statement requestConfig={requestConfig} canSync={BankingService.canMoveMoney ()}/>
      </Content>
    </Container>
  )
}
